<template>
  <a-dropdown
    v-model:open="isEditing"
    :trigger="['click']"
    placement="rightTop"
  >
    <div
      class="flex items-center gap-2 p-2 cursor-pointer dropdown_item"
      @click.stop="handleEdit"
    >
      <ThemeImage
        :icon="imageNames.EDIT"
        alt="edit_icon"
        class="size-5"
      />
      <span class="text-sm text_color">Edit property</span>
      <ThemeImage
        :icon="imageNames.RIGHT_ARROW"
        alt="arrow_right"
        class="size-3 ml-auto"
      />
    </div>
    <template #overlay>
      <div class="formula_edit_dropdown_content">
        <div class="flex flex-col w-[450px] p-4" @click.stop>
          <div class="flex items-center justify-between mb-4">
            <span class="text-sm text_color font-semibold">Edit Formula</span>
          </div>

          <SkeletonLoader v-if="isLoading" page="manage_columns" />

          <div v-else>
            <FormulaBuilder
              v-model="formulaValue"
              :number-attributes="filteredNumberAttributes"
              :config-key="formulaConfigKeys.COLUMN_HEADER"
            />

            <div class="flex justify-end gap-2 mt-4">
              <a-button class="secondary_medium" @click.stop="exitEditMode">
                Cancel
              </a-button>
              <a-button
                class="primary_medium"
                :loading="isSaving"
                @click.stop="save"
              >
                Save
              </a-button>
            </div>
          </div>
        </div>
      </div>
    </template>
  </a-dropdown>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import { message } from "ant-design-vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import { formulaConfigKeys } from "@/src/constants/formulaConstants.js";
import ThemeImage from "@/src/components/ThemeImage.vue";
import SkeletonLoader from "@/src/components/SkeletonLoader.vue";
import FormulaBuilder from "@/src/components/formula_components/FormulaBuilder.vue";
import CRMService from "@/src/api/CRMService.js";
import { useStore } from "vuex";

const store = useStore();

const props = defineProps({
  propertyMeta: {
    type: Object,
    default: null,
  },
  entityType: {
    type: String,
    required: true,
  },
  projectId: {
    type: [String, Number],
    default: null,
  },
  service: {
    type: String,
    default: null,
  },
  isDynamicObject: {
    type: Boolean,
    default: false,
  },
  numberAttributes: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(["property-updated", "close"]);

const isEditing = ref(false);
const isLoading = ref(false);
const isSaving = ref(false);
const formulaValue = ref("");

const filteredNumberAttributes = computed(() => {
  if (!props.numberAttributes) return [];
  return props.numberAttributes.filter(
    (attr) => attr.slug !== props.propertyMeta?.slug
  );
});

const handleEdit = async () => {
  if (!props.isDynamicObject) {
    message.warning("Formula editing is only available for custom objects");
    return;
  }

  isEditing.value = true;
  await loadFormulaDetails();
};

const loadFormulaDetails = async () => {
  if (!props.isDynamicObject || props.propertyMeta?.datatype !== "formula") {
    return;
  }

  try {
    isLoading.value = true;

    // First check if formula is already available in propertyMeta
    if (props.propertyMeta?.formula?.original) {
      formulaValue.value = props.propertyMeta.formula.original;
      return;
    }

    const response = await CRMService.getPraticularAttributeDetails(
      props.entityType,
      props.propertyMeta.slug,
      props.service,
    );

    if (response.success && response.attribute?.formula?.original) {
      formulaValue.value = response.attribute.formula.original;
    } else {
      formulaValue.value = "";
    }
  } catch (error) {
    console.error("Error loading formula details:", error);
    message.error("Failed to load formula details");
  } finally {
    isLoading.value = false;
  }
};

const exitEditMode = () => {
  isEditing.value = false;
  formulaValue.value = "";
};

const refreshSchemaStore = async () => {
  await store.dispatch("schema/refreshSchema", {
    slug: props.entityType,
    projectId: props.projectId || null,
    service: props.service || null,
  });
};

const save = async () => {
  if (!props.isDynamicObject || !props.entityType || !props.propertyMeta?.slug) {
    message.error("Missing required data for formula update");
    return;
  }

  if (!formulaValue.value || !formulaValue.value.trim()) {
    message.error("Please build a valid formula using the dropdowns");
    return;
  }

  try {
    isSaving.value = true;
    const payload = {
      ...props.propertyMeta,
      formula: formulaValue.value,
      created_by_id: Number(localStorage.getItem("userId")) || 1,
    };

    const response = await CRMService.updateSchemaObjectAttribute(
      props.entityType,
      props.propertyMeta.slug,
      payload,
      props.service,
    );

    if (response.success) {
      await refreshSchemaStore();
      message.success("Formula updated successfully");

      emit("property-updated", {
        propertyMeta: props.propertyMeta,
        newName: props.propertyMeta.name,
        response: response,
        isFormulaChange: true,
      });

      exitEditMode();
      emit("close");
    } else {
      message.error(response.error || "Failed to update formula");
    }
  } catch (error) {
    console.error("Error updating formula:", error);
    message.error("Failed to update formula");
  } finally {
    isSaving.value = false;
  }
};
</script>

<style>
.formula_edit_dropdown_content {
  background: var(--dropdown-bg);
  border-radius: 8px;
  box-shadow: var(--dropdown-boxshadow);
  width: fit-content;
  max-height: 600px;
  overflow-y: auto;
}
</style>
